import React, {useState, useEffect, useCallback} from 'react';
import PropTypes from "prop-types";
import {useSelector} from "react-redux";
import dayjs from 'dayjs';
import TextField from "@mui/material/TextField";
import Stack from '@mui/material/Stack';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import ArgoAppBarHook from "../common/ArgoAppBarHook";
import ArgoToolbarHook from "../common/ArgoToolbarHook";
import ArgoTextFieldHook from "../common/ArgoTextFieldHook";
import * as validate from "../../utilities/validate";
import * as TEMPLATE from "../../constants/templateConstants";
import TemplateApi from "../../api/TemplateApi";


const START_DATE_ERROR_TEXT = "Start date is required";
const INVALID_START_DATE = "Start date is invalid"
const INVALID_END_DATE = "End date is invalid"
const NOTE_ERROR_TEXT = "Note is required";


const QuestionnairePublishOrChangeDateDrawerHook = (props) => {
    const {
        closeDrawer,
        template,
        startDate: propsStartDate,
        endDate: propsEndDate,
        expirationDays: propsExpirationDays,
        expirationDescription: propsExpirationDescription,
        title,
        qualifier,
        buttonLabel,
        action,
        currentStartDate,
        setLoadingState = () => {},
        mode
    } = props;

    const filter = useSelector(state => state.filter[TEMPLATE.FILTER.current.questionnaire]);
    const versionsFilter = useSelector(state => state.filter[TEMPLATE.FILTER.versions.questionnaire]);

    const [startDateErrorText, setStartDateErrorText] = useState(buttonLabel === "PUBLISH" ? START_DATE_ERROR_TEXT : " ");
    const [endDateErrorText, setEndDateErrorText] = useState(" ");
    const [noteErrorText, setNoteErrorText] = useState(NOTE_ERROR_TEXT);
    const [toolbarErrorText, setToolbarErrorText] = useState("");
    const [startDate, setStartDate] = useState(validate.notNull(propsStartDate) ? dayjs(propsStartDate).format("YYYY-MM-DD") : null);
    const [endDate, setEndDate] = useState(validate.notNull(propsEndDate) ? dayjs(propsEndDate).format("YYYY-MM-DD") : null);
    const [note, setNote] = useState("");
    const [dateDTO, setDateDTO] = useState({
        startDate: propsStartDate,
        endDate: propsEndDate,
        expirationDays: propsExpirationDays,
        expirationDescription: propsExpirationDescription,
        displayShortForm: false,
    });
    const [publishedDates, setPublishedDates] = useState([]);

    const checkForErrorsOnPage = useCallback(() => {
        let toolbarErrorTextArray = [];

        if (startDateErrorText.trim() !== "") {
            toolbarErrorTextArray.push(startDateErrorText);
        }
        if (endDateErrorText.trim() !== "") {
            toolbarErrorTextArray.push(endDateErrorText);
        }
        if (noteErrorText.trim() !== "") {
            toolbarErrorTextArray.push(noteErrorText);
        }

        let dateHasBeenUsed = (publishedDates.indexOf(startDate) > -1);

        if (dateHasBeenUsed) {
            toolbarErrorTextArray.push("Start date cannot be the same as another version");
        }

        setToolbarErrorText(toolbarErrorTextArray.join(",  "));
    }, [startDateErrorText, endDateErrorText, noteErrorText, publishedDates, startDate]);

    useEffect(() => {
        let publishedTemplates = [];
        let newPublishedDates = [];

        TemplateApi.getQuestionnaireVersions(template.templateKey).then(data => {
            publishedTemplates = data.filter(tmpl => tmpl.startDate !== null)

            for(let i = 0; i < publishedTemplates.length; i++) {
                if (publishedTemplates[i].id !== template.id) {
                    newPublishedDates.push(publishedTemplates[i].startDate);
                }
            }

            setPublishedDates(newPublishedDates);
        })
    }, [template]);

    useEffect(() => {
        checkForErrorsOnPage();
    }, [checkForErrorsOnPage]);

    const handleToolbarButtonClick = (event, fieldName, buttonTitle) => {

        switch (buttonTitle) {
            case "PUBLISH":
            case "SUBMIT":
                setLoadingState();
                action(template.id, {...dateDTO, notes: note}, template.templateKey, filter, versionsFilter);
                break;
            case "CANCEL":
                closeDrawer();
                break;
            // no default
        }
    };

    const onDateFieldChange = useCallback((fieldName, value, message) => {
        let newDateDTO = {...dateDTO};

        let newStartDateErrorText = startDateErrorText;
        let newEndDateErrorText = endDateErrorText;

        let newStartDate = fieldName === "startDate" ? dayjs(value) : (startDate === null ? null: dayjs(startDate));
        let newEndDate = fieldName === "endDate" ? dayjs(value) : (endDate === null ? null: dayjs(endDate));

        if (fieldName === "startDate") {
            if ((newStartDate && newEndDate) && (newStartDate > newEndDate)) {
                newDateDTO["endDate"] = null;
            }

            newDateDTO[fieldName] = dayjs(value).format("YYYY-MM-DD");
        }
        else {
            if (value === null) {
                newEndDate = null
                newDateDTO["endDate"] = newEndDate;
            }
            else newDateDTO[fieldName] = dayjs(value).format("YYYY-MM-DD");
        }

        if (newStartDate === null) {
            newStartDateErrorText = START_DATE_ERROR_TEXT;
        }

        if (fieldName === "startDate" && newStartDate !== null && isNaN(newStartDate.$d?.getTime())) {
            newStartDateErrorText = message ? message : INVALID_START_DATE;
        }
        else if (fieldName === "startDate") newStartDateErrorText = " "

        if (fieldName === "endDate") {
            if (newEndDate !== null && isNaN(newEndDate.$d?.getTime())) {
                newEndDateErrorText = message ? message : INVALID_END_DATE;
            }
            else if (newEndDate === null) newEndDateErrorText = message
            else if (newEndDate.$d < newStartDate.$d) newEndDateErrorText = INVALID_END_DATE;
            else if (newEndDate.$d > newStartDate.$d) newEndDateErrorText = " ";
        }

        setStartDate(newDateDTO["startDate"]);
        setEndDate(newDateDTO["endDate"] === undefined ? null : newDateDTO["endDate"]);
        setStartDateErrorText(newStartDateErrorText);
        setEndDateErrorText(newEndDateErrorText);
        setDateDTO(newDateDTO);
    }, [dateDTO, startDateErrorText, endDateErrorText, startDate, endDate]);

    const onTextChange = useCallback((event, fieldName) => {
        let value = event.target.value;

        let errorText = " ";

        if (fieldName === "note") {
            errorText = (value.trim() === "") ? NOTE_ERROR_TEXT : " ";
            setNote(value);
            setNoteErrorText(errorText);
        }
    }, []);

    return (
        <div>

            <ArgoAppBarHook
                title={mode}
                titleFor={title}
                qualifierFor={qualifier}
                showMenuIconButton={false}
                noIcon={true}
                isDrawer={true}
                width={questionnairePublishOrChangeDateDrawerProps.width}
            />

            <div style={{height: '64px'}}></div>

            <ArgoToolbarHook
                fileName="QuestionnairePublishOrChangeDateDrawer"
                fieldName="publish-submit-or-cancel"
                buttonTitles={[buttonLabel, "CANCEL"]}
                toolbarErrorText={toolbarErrorText}
                onClick={handleToolbarButtonClick}
                width={questionnairePublishOrChangeDateDrawerProps.width}
            />

            <div style={{marginLeft: "20px", marginRight: "20px", marginTop: "30px"}}>

                <div style={{height: "30px"}}>&nbsp;</div>

                <Stack spacing={2}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DesktopDatePicker id="QuestionnairePublishOrChangeDateDrawer-start-date"
                                           fullWidth
                                           value={startDate}
                                           onChange={e => onDateFieldChange("startDate", e, e === null ? START_DATE_ERROR_TEXT : null)}
                                           label="Start date *"
                                           container="inline"
                                           autoFocus
                                           renderInput={(params) => <TextField  {...params} variant="standard"/>}
                                           helperText={startDateErrorText}
                        />

                        <DesktopDatePicker id="QuestionnairePublishOrChangeDateDrawer-end-date"
                                           fullWidth
                                           onChange={e => onDateFieldChange("endDate", e, e === null ? " " : null)}
                                           value={endDate}
                                           label="End date"
                                           disabled={startDate === null}
                                           minDate={startDate}
                                           renderInput={(params) => <TextField  {...params} variant="standard"/>}
                                           error={false}
                        />
                    </LocalizationProvider>

                    <ArgoTextFieldHook
                        fileName="QuestionnairePublishOrChangeDateDrawer"
                        fieldName="note"
                        label="Note *"
                        value={note}
                        multiline={true}
                        onChange={onTextChange}
                        errorText={noteErrorText}
                        maxLength={150}
                    />

                </Stack>

                <div style={{fontSize: "12px"}}>* Indicates required field</div>

                <div style={{height: '20px'}}></div>

                {currentStartDate !== "" ?
                    <div>
                        <div style={{float: "left", paddingRight: "10px", color: "gray"}}>Previous Start Date:</div>
                        <div style={{float: "left"}}>{currentStartDate}</div>
                    </div> : null
                }

            </div>

        </div>
    );
};

QuestionnairePublishOrChangeDateDrawerHook.propTypes = {
    closeDrawer: PropTypes.func,
    template: PropTypes.object,
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    expirationDays: PropTypes.string,
    expirationDescription: PropTypes.string,
    title: PropTypes.string,
    qualifier: PropTypes.string,
    buttonLabel: PropTypes.string,
    action: PropTypes.func,
    currentStartDate: PropTypes.string,
    setLoadingState: PropTypes.func,
    mode: PropTypes.string
};

const questionnairePublishOrChangeDateDrawerProps = {
    opensecondary: true,
    width: "55%",
    docked: false,
    anchor: "right"
};

export default QuestionnairePublishOrChangeDateDrawerHook;
export {questionnairePublishOrChangeDateDrawerProps};